import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useQrAnalytics, useUpdateQrCode, qrErrorMessage, type QrCodeRow } from "@/hooks/useQrCodes";
import { QrImage, QrDownloads } from "./QrImage";
import { DayBars } from "./DayBars";

const DEVICE_LABEL: Record<string, string> = { ios: "iPhone", android: "Android", desktop: "Computer", other: "Other" };
const PERIODS = [30, 90, 365] as const;

const dayLabel = (key: string) =>
  new Date(`${key}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

function Ranked({ title, rows }: { title: string; rows: { label: string; n: number }[] }) {
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">{title}</div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nothing yet</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {rows.map((r) => (
            <li key={r.label} className="flex justify-between gap-3">
              <span className="truncate">{r.label}</span>
              <span className="tabular-nums text-muted-foreground">{r.n}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function QrCodeDetail({ row, onClose }: { row: QrCodeRow | null; onClose: () => void }) {
  const [days, setDays] = useState<(typeof PERIODS)[number]>(30);
  const [name, setName] = useState("");
  const [destination, setDestination] = useState("");
  const analytics = useQrAnalytics(row?.id ?? null, days);
  const update = useUpdateQrCode();

  useEffect(() => {
    setName(row?.name ?? "");
    setDestination(row?.destinationUrl ?? "");
  }, [row?.id, row?.name, row?.destinationUrl]);

  const save = async (body: { name?: string; destination?: string; paused?: boolean; archived?: boolean }, done: string) => {
    if (!row) return;
    try {
      await update.mutateAsync({ id: row.id, ...body });
      toast.success(done);
      if (body.archived) onClose();
    } catch (e: any) {
      toast.error(qrErrorMessage(e, "Could not save"));
    }
  };

  const a = analytics.data;
  const dirty = row && (name.trim() !== row.name || destination.trim() !== row.destinationUrl);
  const recordHref = row?.contactId ? `/contacts/${row.contactId}` : row?.companyId ? `/companies/${row.companyId}` : null;

  return (
    <Sheet open={Boolean(row)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
        {row && (
          <div className="space-y-6">
            <SheetHeader className="space-y-1 border-b border-border pb-4">
              <SheetTitle className="pr-8 text-lg leading-tight">{row.name}</SheetTitle>
              <SheetDescription>
                {row.contactName || row.companyName ? (
                  <>For {recordHref ? <Link href={recordHref} className="text-primary-text hover:underline">{row.contactName ?? row.companyName}</Link> : row.contactName ?? row.companyName}</>
                ) : (
                  "Placement code"
                )}
                {row.pausedAt && " · Paused: scans go to the homepage"}
              </SheetDescription>
            </SheetHeader>

            <section className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <QrImage url={row.url} size={168} />
              <div className="min-w-0 flex-1 space-y-3">
                <code className="block truncate text-xs text-muted-foreground">{row.url}</code>
                <QrDownloads url={row.url} name={row.name} />
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Scans</div>
                <div className="flex gap-1">
                  {PERIODS.map((p) => (
                    <button
                      key={p}
                      onClick={() => setDays(p)}
                      className={cn("rounded px-2 py-0.5 text-xs", p === days ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted")}
                    >
                      {p === 365 ? "1y" : `${p}d`}
                    </button>
                  ))}
                </div>
              </div>
              {!a ? (
                <Skeleton className="h-28 w-full" />
              ) : (
                <>
                  <div className="flex items-baseline gap-4 text-sm">
                    <span><span className="text-2xl font-semibold tabular-nums">{a.totals.scans}</span> scans</span>
                    <span className="text-muted-foreground"><span className="tabular-nums">{a.totals.unique}</span> devices</span>
                  </div>
                  <DayBars
                    values={a.daily}
                    height={72}
                    labels={a.dayKeys.map((k, i) => `${dayLabel(k)}: ${a.daily[i]} scan${a.daily[i] === 1 ? "" : "s"}`)}
                  />
                  <div className="flex justify-between text-[11px] text-muted-foreground">
                    <span>{dayLabel(a.dayKeys[0])}</span>
                    <span>Today</span>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-3">
                    <Ranked title="Device" rows={a.devices.map((d) => ({ label: DEVICE_LABEL[d.device] ?? d.device, n: d.n }))} />
                    <Ranked title="Where" rows={a.places.map((p) => ({ label: p.place, n: p.n }))} />
                    <Ranked title="Network" rows={a.networks.map((n) => ({ label: n.org, n: n.n }))} />
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Location and network come from the scanner's internet connection, so a phone on cellular shows its
                    carrier. {a.totals.botScans > 0 && `${a.totals.botScans} link-preview or bot hits were left out.`}
                  </p>
                </>
              )}
            </section>

            <section className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Recent scans</div>
              {a && a.recent.length === 0 && <p className="text-xs text-muted-foreground">No scans yet.</p>}
              <ul className="divide-y divide-border text-sm">
                {a?.recent.map((s, i) => (
                  <li key={i} className="flex justify-between gap-3 py-1.5">
                    <span className="truncate">
                      {DEVICE_LABEL[s.device] ?? s.device}
                      {s.place && <span className="text-muted-foreground"> · {s.place}</span>}
                      {s.org && <span className="text-muted-foreground"> · {s.org}</span>}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {new Date(s.scannedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="space-y-3 border-t border-border pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="qr-edit-name">Name</Label>
                <Input id="qr-edit-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="qr-edit-dest">Sends people to</Label>
                <Input id="qr-edit-dest" value={destination} onChange={(e) => setDestination(e.target.value)} />
                <p className="text-xs text-muted-foreground">Printed copies keep working and will point here instead.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={!dirty || update.isPending}
                  onClick={() => save({ name: name.trim(), destination: destination.trim() }, "Saved")}
                >
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => save({ paused: !row.pausedAt }, row.pausedAt ? "Resumed" : "Paused")}>
                  {row.pausedAt ? "Resume" : "Pause"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => save({ archived: !row.archivedAt }, row.archivedAt ? "Restored" : "Archived")}
                >
                  {row.archivedAt ? "Restore" : "Archive"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Paused and archived codes still redirect people to the homepage, so nothing printed ever breaks.
              </p>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
